import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Hook para manejar el scroll infinito
 *
 * @param {boolean} isLoading - Estado de carga
 * @param {boolean} isSearching - Estado de búsqueda
 * @returns {object} Objeto con la referencia del elemento y el offset
 */
const useInfinityScroll = (isLoading, isSearching) => {
  const [offset, setOffset] = useState(0); // Offset de la paginación
  const loadMoreRef = useRef(null); // Referencia del elemento

  /**
   * Maneja la observación del scroll
   *
   * @param {array} entries - Entradas del observer
   * @returns {void}
   */
  const handleObserver = useCallback(
    (entries) => {
      const [target] = entries;
      if (target.isIntersecting && !isLoading && !isSearching) {
        setOffset((prevOffset) => prevOffset + 15);
      }
    },
    [isLoading, isSearching]
  );

  // Observa el scroll
  useEffect(() => {
    const option = {
      root: null,
      rootMargin: "50px",
      threshold: 0.5,
    };

    const observer = new IntersectionObserver(handleObserver, option);

    if (loadMoreRef.current) {
      // Observa el elemento
      observer.observe(loadMoreRef.current);
    } else {
      // Intenta observar el elemento después de un segundo
      setTimeout(() => {
        if (loadMoreRef.current) {
          observer.observe(loadMoreRef.current);
        }
      }, 1000);
    }

    return () => observer.disconnect();
  }, [handleObserver]);

  return { loadMoreRef, offset, setOffset };
};

export default useInfinityScroll;
